import { useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { RequireOperator } from './auth/RequireOperator';
import { api } from '../api/client';
import { isDemoMode } from './demoMode';
import { DemoBanner } from './DemoBanner';

type NavItem = { id: string; label: string; icon: string; to: string; badge?: string | number };
function buildNav(counts: { orders: number; logistics: number } | null): NavItem[] {
  return [
    { id: 'dash', label: 'Dashboard', icon: '◐', to: '/admin' },
    { id: 'orders', label: 'Zamówienia', icon: '▤', to: '/admin/zamowienia', badge: counts?.orders },
    { id: 'tickets', label: 'Bilety', icon: '◈', to: '/admin/bilety' },
    { id: 'logi', label: 'Logistyka', icon: '▶', to: '/logistyka', badge: counts?.logistics },
  ];
}

const SETTINGS_NAV = [
  { id: 'produkty', label: 'Produkty', icon: '▦', to: '/admin/produkty' },
  { id: 'ustawienia', label: 'Ustawienia sklepu', icon: '⚙', to: '/admin/ustawienia' },
];

const TITLES: Record<string, string> = {
  '/admin': 'Dashboard',
  '/admin/zamowienia': 'Zamówienia',
  '/admin/zamowienia/nowe': 'Zamówienia',
  '/admin/bilety': 'Bilety',
  '/admin/produkty': 'Produkty',
  '/admin/ustawienia': 'Ustawienia sklepu',
};

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const [counts, setCounts] = useState<{ orders: number; logistics: number } | null>(null);
  const isOrdersPath = location.pathname === '/admin/zamowienia';
  const urlQ = isOrdersPath ? searchParams.get('q') ?? '' : '';
  const [searchValue, setSearchValue] = useState(urlQ);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setSearchValue(urlQ);
  }, [urlQ]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    let stop = false;
    const load = () =>
      api
        .get<{ orders: number; logistics: number }>('/admin/counts')
        .then((c) => !stop && setCounts(c))
        .catch(() => {});
    // Neon suspends compute only after 5 idle minutes, so polling from a tab nobody is
    // looking at keeps the database billed around the clock.
    let t: ReturnType<typeof setInterval> | undefined;
    const sync = () => {
      clearInterval(t);
      t = undefined;
      if (document.visibilityState !== 'visible') return;
      void load();
      t = setInterval(load, 30_000);
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => {
      stop = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [auth.status]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchValue.trim();
    const target = trimmed ? `/admin/zamowienia?q=${encodeURIComponent(trimmed)}` : '/admin/zamowienia';
    navigate(target);
  };

  if (auth.status === 'loading' || auth.status === 'unauthenticated') {
    return (
      <RequireOperator>
        <Outlet />
      </RequireOperator>
    );
  }

  const title =
    TITLES[location.pathname] ??
    (location.pathname.startsWith('/admin/zamowienia/') ? location.pathname.split('/').pop() : 'Operacja Teneryfa');

  return (
    <div className={'admin' + (navOpen ? ' nav-open' : '')}>
      <Sidebar operatorName={auth.operator.displayName} onLogout={auth.logout} counts={counts} />
      {navOpen && <div className="admin-backdrop" onClick={() => setNavOpen(false)} />}
      <header className="admin-top">
        <div className="admin-top-title">
          <button
            type="button"
            className="admin-burger"
            aria-label="Menu"
            aria-controls="admin-nav"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            ☰
          </button>
          <h1>{title}</h1>
          <span
            className="mono admin-route-tag"
            style={{
              fontSize: 11,
              color: 'var(--admin-muted)',
              padding: '3px 8px',
              background: '#FAF0E0',
              border: '1px solid #F0D9B8',
            }}
          >
            TRASA: TENERYFA · 28.11.2026
          </span>
        </div>
        <div className="admin-top-actions">
          <form onSubmit={submitSearch} className="admin-search">
            <input
              className="input-a"
              enterKeyHint="search"
              placeholder="Szukaj — bilet, nazwisko, paczkomat, OPT-…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </form>
          <NavLink className="btn-a primary" to="/admin/zamowienia/nowe">
            <span>
              + Nowe<span className="hide-phone"> zamówienie</span>
            </span>
          </NavLink>
        </div>
      </header>
      <main className="admin-main">
        {isDemoMode() && <DemoBanner />}
        <Outlet />
      </main>
    </div>
  );
}

function Sidebar({
  operatorName,
  onLogout,
  counts,
}: {
  operatorName: string;
  onLogout: () => void;
  counts: { orders: number; logistics: number } | null;
}) {
  const nav = buildNav(counts);
  const initials = operatorName
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'OP';
  return (
    <aside className="admin-side" id="admin-nav">
      <Link
        to="/admin"
        style={{
          padding: '4px 10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        <img
          src="/logo.png"
          alt=""
          style={{ width: 44, height: 'auto', filter: 'drop-shadow(0 1px 0 #000)' }}
        />
        <div style={{ lineHeight: 1.05 }}>
          <div
            style={{
              fontFamily: 'var(--display)',
              textTransform: 'uppercase',
              fontSize: 13,
              letterSpacing: '.06em',
              color: 'var(--orange-soft)',
            }}
          >
            Operacja
            <br />
            Teneryfa
          </div>
          <div
            style={{
              fontSize: 9,
              letterSpacing: '.18em',
              color: 'rgba(232,226,213,.4)',
              fontFamily: 'var(--mono)',
              marginTop: 3,
            }}
          >
            ADMIN · v1.0
          </div>
        </div>
      </Link>

      <div className="nav-section">SPRZEDAŻ</div>
      {nav.map((item) => (
        <NavItem key={item.id} {...item} />
      ))}

      <div className="nav-section">USTAWIENIA</div>
      {SETTINGS_NAV.map((item) => (
        <NavItem key={item.id} {...item} />
      ))}

      <div
        style={{
          marginTop: 'auto',
          padding: 10,
          borderTop: '1px solid #2A1B12',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 99,
            background: 'var(--orange)',
            color: 'var(--ink)',
            fontFamily: 'var(--display)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
          }}
        >
          {initials}
        </div>
        <div style={{ lineHeight: 1.2, flex: 1 }}>
          <div>{operatorName}</div>
          <div style={{ opacity: 0.5, fontSize: 10 }} className="mono">
            ADMIN · TENERYFA
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          title="Wyloguj"
          style={{
            background: 'transparent',
            border: '1px solid #2A1B12',
            color: 'inherit',
            padding: '4px 8px',
            fontSize: 11,
            cursor: 'pointer',
          }}
        >
          ⏻
        </button>
      </div>
    </aside>
  );
}

function NavItem({
  to,
  icon,
  label,
  badge,
}: {
  to: string;
  icon: string;
  label: string;
  badge?: string | number;
}) {
  return (
    <NavLink
      to={to}
      end={to === '/admin'}
      className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}
    >
      <span>
        {icon} {label}
      </span>
      {badge !== undefined && (
        <span
          style={
            label === 'Logistyka'
              ? {
                  marginLeft: 'auto',
                  fontSize: 10,
                  padding: '1px 6px',
                  background: 'var(--orange)',
                  color: '#000',
                  borderRadius: 99,
                  fontWeight: 700,
                }
              : { marginLeft: 'auto', fontSize: 11, opacity: 0.7 }
          }
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}
